import { Controller, useFormContext } from 'react-hook-form';
import { TextField, type TextFieldProps } from '@mui/material';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';

import { DrugField, type DrugFieldProps } from '@/components/form-controls/DrugField';

export type RHFDrugFieldProps = Omit<DrugFieldProps, 'renderInput'> & {
  name: string;
  label?: string;
  helperText?: React.ReactNode;
  TextFieldProps?: Omit<TextFieldProps, 'label' | 'error' | 'helperText'>;
};

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T | null) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === 'function') ref(node as T);
      else (ref as React.MutableRefObject<T | null>).current = node;
    }
  };
}

export const RHFDrugField = (props: RHFDrugFieldProps) => {
  const {
    name,
    label,
    helperText,
    TextFieldProps,
    forSpecificationTemplate = false,
    marketingOrgIds,
    ...restOfProps
  } = props;

  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const { ref, onChange, ...restFieldProps } = field;

        const reassignedOnChange: DrugFieldProps['onChange'] = (_event, value) => {
          onChange(value);
        };

        return (
          <DrugField
            {...restFieldProps}
            {...restOfProps}
            forSpecificationTemplate={forSpecificationTemplate}
            marketingOrgIds={marketingOrgIds}
            onChange={reassignedOnChange}
            renderInput={(inputParams: AutocompleteRenderInputParams) => (
              <TextField
                {...TextFieldProps}
                {...inputParams}
                error={!!error}
                label={label}
                placeholder="Поиск"
                helperText={error ? error.message : helperText}
                inputRef={mergeRefs(ref, inputParams.inputProps.ref)}
              />
            )}
          />
        );
      }}
    />
  );
};
